import type { GridApi, SelectionChangedEvent } from 'ag-grid-community';

function getLoadedRowsById<T>(api: GridApi<T>): Map<string, T> {
  const loadedRows = new Map<string, T>();
  api.forEachNode((node) => {
    if (node.group || node.stub || !node.data) {
      return;
    }
    loadedRows.set(String(node.id), node.data);
  });
  return loadedRows;
}

/**
 * Returns selected rows for SSRM, including header select-all, constrained to loaded rows.
 * For regular row selection this falls back to `api.getSelectedRows()`.
 *
 * When select-all is active, serverSideState is the source of truth. In AG Grid SSRM,
 * `getSelectedRows()` only returns individually-toggled nodes rather than all implicitly
 * selected rows, which would produce an incorrect result when the header select-all is used.
 *
 * The returned array only contains rows currently loaded in the SSRM cache. To get an
 * up-to-date count as the user scrolls and more blocks load, call this function again from
 * an `onModelUpdated` handler using the last stored `serverSideState`.
 */
export function resolveSsrmSelectedRows<T>(
  api: GridApi<T>,
  serverSideState: SelectionChangedEvent<T>['serverSideState']
): T[] {
  if (serverSideState && 'selectAll' in serverSideState && Array.isArray(serverSideState.toggledNodes)) {
    const loadedById = getLoadedRowsById(api);
    const toggledIds = new Set(
      serverSideState.toggledNodes.filter((id): id is string => typeof id === 'string')
    );

    if (serverSideState.selectAll === true) {
      // All loaded rows except individually deselected ones
      return Array.from(loadedById.entries())
        .filter(([id]) => !toggledIds.has(id))
        .map(([, row]) => row);
    }

    // Only the explicitly selected rows
    return Array.from(toggledIds)
      .map((id) => loadedById.get(id))
      .filter((row): row is T => !!row);
  }

  // Fallback for non-SSRM or when serverSideState is absent
  return api.getSelectedRows() ?? [];
}

